import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import {
  ArrowLeft,
  Trophy,
  Flame,
  Target,
  Star,
  Crown,
  Medal,
  Award,
  Check,
  Lock
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_3e64acab-2561-42e6-b860-06b1a563df4d/artifacts/5nvw3yv2_6ECA6BBA-F429-4238-A743-EE6BF2D1D33F.png";

const BADGE_ICONS = {
  starter: Target,
  bronze: Medal,
  silver: Medal,
  gold: Crown,
  platinum: Star,
  elite: Crown
};

export default function Achievements() {
  const { user } = useAuth();
  const [badgeData, setBadgeData] = useState(null);
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [badgeRes, achieveRes] = await Promise.all([
        axios.get(`${API}/badges/current`),
        axios.get(`${API}/achievements`)
      ]);
      setBadgeData(badgeRes.data);
      setAchievements(achieveRes.data.achievements || []);
    } catch (error) {
      console.error("Failed to fetch achievements:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary animate-pulse text-2xl font-bold">LOADING...</div>
      </div>
    );
  }

  const BadgeIcon = badgeData?.current_badge ? BADGE_ICONS[badgeData.current_badge.level] : Target;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-5 w-5" /> Back
            </Link>
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Coach Coop" className="h-8 w-8 rounded-sm" />
              <span className="font-bold tracking-tight">ACHIEVEMENTS</span>
            </div>
            <div className="w-16" />
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        {/* Discipline Score */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Card className="bg-gradient-to-br from-primary/20 to-transparent border-primary/50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-sm text-muted-foreground uppercase tracking-wider">Discipline Score</div>
                  <div className="text-5xl font-black text-primary font-mono">{badgeData?.discipline_score || 0}</div>
                </div>
                <div className="text-center">
                  <div 
                    className="w-20 h-20 rounded-full flex items-center justify-center mb-2"
                    style={{ backgroundColor: `${badgeData?.current_badge?.color}20`, border: `2px solid ${badgeData?.current_badge?.color}` }}
                  >
                    <BadgeIcon className="h-10 w-10" style={{ color: badgeData?.current_badge?.color }} />
                  </div>
                  <div className="font-bold text-sm uppercase" style={{ color: badgeData?.current_badge?.color }}>
                    {badgeData?.current_badge?.name}
                  </div>
                </div>
              </div>
              <Progress value={badgeData?.discipline_score || 0} className="h-3" />
            </CardContent>
          </Card>
        </motion.div>

        {/* Streak Badges */}
        <Card className="bg-card border-border mb-8">
          <CardHeader>
            <CardTitle className="uppercase tracking-tighter flex items-center gap-2">
              <Flame className="h-5 w-5 text-orange-500" />
              Streak Badges
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-5 gap-4">
              {[
                { level: "bronze", name: "Bronze", streak: 7, color: "#CD7F32" },
                { level: "silver", name: "Silver", streak: 14, color: "#C0C0C0" },
                { level: "gold", name: "Gold", streak: 30, color: "#FFD700" },
                { level: "platinum", name: "Platinum", streak: 45, color: "#E5E4E2" },
                { level: "elite", name: "Elite", streak: 60, color: "#DC143C" }
              ].map((badge, i) => {
                const earned = (badgeData?.longest_streak || 0) >= badge.streak;
                const Icon = BADGE_ICONS[badge.level];
                return (
                  <motion.div
                    key={badge.level}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: i * 0.1 }}
                    className="text-center"
                  >
                    <div 
                      className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-2 transition-all ${
                        earned ? 'glow-primary' : 'opacity-30'
                      }`}
                      style={{ 
                        backgroundColor: earned ? `${badge.color}20` : '#1A1A1A',
                        border: `2px solid ${earned ? badge.color : '#333'}`
                      }}
                    >
                      <Icon className="h-8 w-8" style={{ color: earned ? badge.color : '#666' }} />
                    </div>
                    <div className="font-bold text-xs uppercase" style={{ color: earned ? badge.color : '#666' }}>
                      {badge.name}
                    </div>
                    <div className="text-xs text-muted-foreground">{badge.streak}+ days</div>
                  </motion.div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Achievements */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="uppercase tracking-tighter flex items-center gap-2">
              <Trophy className="h-5 w-5 text-accent" />
              Achievements ({achievements.filter(a => a.earned).length}/{achievements.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 gap-4">
              {achievements.map((achievement, i) => (
                <motion.div
                  key={achievement.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className={`flex items-center gap-4 p-4 rounded-sm border ${
                    achievement.earned 
                      ? 'border-accent/50 bg-accent/10' 
                      : 'border-border bg-secondary/30 opacity-60'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    achievement.earned ? 'bg-accent/20' : 'bg-secondary'
                  }`}>
                    {achievement.earned ? (
                      <Check className="h-6 w-6 text-accent" />
                    ) : (
                      <Lock className="h-5 w-5 text-muted-foreground" />
                    )}
                  </div>
                  <div>
                    <div className={`font-bold uppercase tracking-tight ${achievement.earned ? 'text-accent' : 'text-muted-foreground'}`}>
                      {achievement.name}
                    </div>
                    <div className="text-sm text-muted-foreground">{achievement.description}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
